import { camelCase, snakeCase, startCase } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, ButtonGroup, CardContent, CircularProgress, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@material-ui/core';
import { Add, Redo, Undo } from '@material-ui/icons';
import { PageTemplateCardOver, PageTemplateDashboard } from '@sierralabs/core-ui';
import { useTrackChanges } from '@sierralabs/track-changes-react';
import { useApplyFormSchemaDelta, useFindFormSchemaDeltas } from '../FormSchema.hooks';
import FormSchemaEditorRow from './FormSchemaEditorRow';
const StyledHeader = styled.div `
  display: flex;
  align-items: center;
  .header-label {
    margin-right: 12px;
    margin-bottom: 20px;
  }
  .action-buttons {
    display: flex;
    margin-left: 8px;
  }
  .saving-status {
    margin-top: 3px;
    min-height: 19px;
    display: flex;
    justify-content: center;
    .save-label {
      margin-left: 8px;
    }
  }
`;
export const FormSchemaEditor = ({ formSchema: initialFormSchema }) => {
    var _a, _b, _c;
    const timeoutRef = useRef(0);
    const [isQueued, setIsQueued] = useState(false);
    const [error, setError] = useState({});
    // const [formSchema, setFormSchema] = useState(initialFormSchema);
    if (!initialFormSchema.config) {
        initialFormSchema.config = {
            name: initialFormSchema.entityName,
            displayName: initialFormSchema.name,
            tableName: snakeCase(initialFormSchema.name),
            columns: []
        };
    }
    // setup track changes
    const trackChanges = useTrackChanges(initialFormSchema);
    const formSchema = trackChanges.final;
    const { formSchemaDeltaList, getFormSchemaDeltas } = useFindFormSchemaDeltas(formSchema.id);
    useEffect(() => getFormSchemaDeltas(), [getFormSchemaDeltas]);
    useEffect(() => {
        if (formSchemaDeltaList) {
            trackChanges.history.undo.unshift(...formSchemaDeltaList.data.map(item => item.delta));
        }
    }, [formSchemaDeltaList, trackChanges.history.undo]);
    // handle applying deltas to server side
    const { updatedResult, isUpdating, updateError, applyFormSchemaDeltas } = useApplyFormSchemaDelta(formSchema.id);
    const { uncommit } = trackChanges;
    useEffect(() => {
        if (updatedResult || updateError) {
            setIsQueued(false);
        }
        if (updateError) {
            // undo commit if error
            uncommit();
        }
    }, [uncommit, updateError, updatedResult]);
    const validateSchema = useCallback(() => {
        // validate schema
        error.name = {
            touched: true,
            message: (formSchema === null || formSchema === void 0 ? void 0 : formSchema.name.trim().length) === 0
                ? 'Schema name is required'
                : undefined
        };
        setError(Object.assign({}, error));
        return Boolean(!error.name.message);
    }, [error, formSchema]);
    const saveSchema = useCallback(() => {
        clearTimeout(timeoutRef.current);
        if (validateSchema()) {
            timeoutRef.current = setTimeout(() => {
                const deltas = trackChanges.commit();
                applyFormSchemaDeltas(deltas);
            }, 2000);
            setIsQueued(true);
        }
    }, [applyFormSchemaDeltas, trackChanges, validateSchema]);
    const columns = ((_a = formSchema === null || formSchema === void 0 ? void 0 : formSchema.config) === null || _a === void 0 ? void 0 : _a.columns) || [];
    return (React.createElement(PageTemplateDashboard, null,
        React.createElement(PageTemplateCardOver, null,
            React.createElement(CardContent, null,
                React.createElement(StyledHeader, null,
                    React.createElement(Typography, { variant: 'subtitle2', className: 'header-label' }, "Schema\u00A0Name:"),
                    React.createElement(TextField, { fullWidth: true, size: 'small', variant: 'outlined', placeholder: 'Form Schema Name', value: (formSchema === null || formSchema === void 0 ? void 0 : formSchema.name) || '', helperText: ((_b = error === null || error === void 0 ? void 0 : error.name) === null || _b === void 0 ? void 0 : _b.message) ||
                            `Entity Name: ${formSchema === null || formSchema === void 0 ? void 0 : formSchema.entityName}; Table Name: ${formSchema === null || formSchema === void 0 ? void 0 : formSchema.tableName}`, error: Boolean((_c = error === null || error === void 0 ? void 0 : error.name) === null || _c === void 0 ? void 0 : _c.message), onChange: event => {
                            if (formSchema) {
                                const name = event.target.value;
                                formSchema.entityName = startCase(camelCase(`Form${formSchema.name}`)).replace(/ /g, '');
                                formSchema.tableName = snakeCase(formSchema.entityName);
                                trackChanges.update('name', name);
                                saveSchema();
                            }
                        } }),
                    React.createElement("div", { className: 'status-layout' },
                        React.createElement(ButtonGroup, { className: 'action-buttons' },
                            React.createElement(Button, { onClick: () => {
                                    const result = trackChanges.undo();
                                    // TOOD: fix undo refresh on column updates
                                    // if (result?.delta?.path?.includes('columns')) {
                                    //   console.log('setFinal', trackChanges.final);
                                    //   setTimeout(() => {
                                    //     trackChanges.setFinal(final => {
                                    //       if (final.config?.columns) {
                                    //         final.config = {
                                    //           ...final.config,
                                    //           columns: [...final.config.columns]
                                    //         };
                                    //       }
                                    //       return { ...final };
                                    //     });
                                    //   });
                                    // }
                                } },
                                React.createElement(Undo, null)),
                            React.createElement(Button, { onClick: () => trackChanges.redo() },
                                React.createElement(Redo, null))),
                        React.createElement("div", { className: 'saving-status' }, (isQueued || isUpdating) && (React.createElement(React.Fragment, null,
                            React.createElement(CircularProgress, { size: 16 }),
                            React.createElement(Typography, { variant: 'caption', className: 'save-label' }, isQueued ? 'Queued...' : 'Saving...'))))))),
            React.createElement(Divider, null),
            React.createElement(TableContainer, null,
                React.createElement(Table, { stickyHeader: true },
                    React.createElement(TableHead, null,
                        React.createElement(TableRow, null,
                            React.createElement(TableCell, null, "Field Name"),
                            React.createElement(TableCell, null, "Property Key"),
                            React.createElement(TableCell, null, "Data Type"),
                            React.createElement(TableCell, null, "Array"),
                            React.createElement(TableCell, null, "Comment / Description"),
                            React.createElement(TableCell, null))),
                    React.createElement(TableBody, null,
                        columns.map((column, index) => (React.createElement(FormSchemaEditorRow, { key: index, column: column, onChange: changedColumn => {
                                const keys = Object.keys(changedColumn);
                                keys.forEach(key => {
                                    if (changedColumn[key] !== column[key]) {
                                        trackChanges.update(`config.columns[${index}].${key}`, changedColumn[key]);
                                        saveSchema();
                                    }
                                });
                            }, onDelete: () => {
                                trackChanges.delete(`config.columns[${index}]`);
                                saveSchema();
                            } }))),
                        React.createElement(TableRow, null,
                            React.createElement(TableCell, { colSpan: 6 },
                                React.createElement(Button, { variant: 'outlined', color: 'primary', startIcon: React.createElement(Add, null), onClick: () => {
                                        if (!(formSchema === null || formSchema === void 0 ? void 0 : formSchema.config))
                                            return;
                                        trackChanges.insert('config.columns', {
                                            displayName: '',
                                            name: '',
                                            type: 'text'
                                        });
                                    } }, "Add Field")))))))));
};
export default FormSchemaEditor;
//# sourceMappingURL=FormSchemaEditor.js.map